import { getFormattedTimeWindow } from '@modules/charts/utils';
import { EMPTY_ARRAY } from '@modules/common/constants';
import { getDateRangeEndTime, getDateRangeWindow } from '@modules/common/utils';
import { AggregateFormulaValue } from '@modules/quantify/components/AggregateFormulaResultTable/AggregateFormulaValue';
import { ShowFeedbackDrawerFromTableOptions } from '@modules/quantify/components/QuantifyReportTableWidget/types';
import {
  QuantifyQuery,
  QuantifyReportDataPoint,
} from '@modules/quantify/store/types';
import { getQuantifyTableRowsVariants } from '@modules/quantify/utils';
import { Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import {
  EqlField,
  QuantifyVisualisationType,
  TimeUnit,
} from 'generated/graphql';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import { renderDynamicColumnCell } from './renderDynamicColumnCell';

interface getDynamicColumnsOptions {
  dataPoints: QuantifyReportDataPoint[];
  activeVisualisationType?: QuantifyVisualisationType;
  aggregatePeriod?: TimeUnit;
  shouldShowAnomalies?: boolean;
  onFeedbackCountClick: (params: ShowFeedbackDrawerFromTableOptions) => void;
  groupBy: QuantifyQuery['groupByField'];
  formulaID?: EQLFunctionName;
  measuredAsCriteria: EqlField | undefined;
  shouldRemoveFileUploadPrefix: boolean;
  isUnifiedFieldEnabled: boolean;
  isSingleRecordQuery: boolean;
}

export function getDynamicColumns({
  dataPoints,
  activeVisualisationType,
  onFeedbackCountClick,
  aggregatePeriod,
  shouldShowAnomalies,
  groupBy,
  formulaID,
  measuredAsCriteria,
  shouldRemoveFileUploadPrefix,
  isUnifiedFieldEnabled,
  isSingleRecordQuery,
}: getDynamicColumnsOptions) {
  if (!dataPoints?.length) {
    return EMPTY_ARRAY;
  }

  if (!dataPoints[0].variants) {
    return EMPTY_ARRAY;
  }

  const { variantLabels, variantLabelById } =
    getQuantifyTableRowsVariants(dataPoints);

  const getVariantLabel = (
    variantId: string,
    variantLabelById: Record<string, string>,
  ) => {
    if (activeVisualisationType === QuantifyVisualisationType.Trend) {
      const sortedTimestamps = Object.values(variantLabelById)
        .map(Number)
        .sort((a, b) => a - b);

      const firstTimeWindow =
        sortedTimestamps.length > 0
          ? dayjs(sortedTimestamps[0]).tz()
          : undefined;
      const lastTimeWindow =
        sortedTimestamps.length > 0
          ? dayjs(sortedTimestamps[sortedTimestamps.length - 1]).tz()
          : undefined;

      const startDate = dayjs(Number(variantId)).tz();
      const endDate = getDateRangeEndTime(startDate, aggregatePeriod);

      return getFormattedTimeWindow({
        startDate,
        endDate,
        aggregatePeriod,
        firstTimeWindow,
        lastTimeWindow,
      });
    }

    return variantLabelById[variantId];
  };

  const anomalousDataPoint = shouldShowAnomalies
    ? dataPoints.find((dp) => dp.isAnomalous)
    : undefined;

  const renderHeader = (variantId: string, variantLabel: string) => {
    return (
      <Typography
        variant="subtitle2"
        color={
          !!anomalousDataPoint?.variants?.[variantId]?.anomalyValue
            ? 'error.light'
            : 'text.primary'
        }
      >
        {variantLabel || null}
      </Typography>
    );
  };

  const renderCell = (params: GridRenderCellParams, variantId: string) => {
    if (isSingleRecordQuery) {
      const variant = params.row?.variants?.[variantId];

      const dateRange =
        activeVisualisationType === QuantifyVisualisationType.Trend
          ? getDateRangeWindow({
              timestamp: Number(variantId),
              aggregatePeriod,
            })
          : undefined;

      return (
        <AggregateFormulaValue
          value={variant?.value ?? 0}
          nps={variant?.nps}
          csat={variant?.csat}
          mean={variant?.mean}
          dateRange={dateRange}
          onFeedbackCountClick={onFeedbackCountClick}
        />
      );
    }

    return renderDynamicColumnCell({
      activeVisualisationType,
      aggregatePeriod,
      onFeedbackCountClick,
      params,
      variantId,
      groupBy,
      formulaID,
      measuredAsCriteria,
      shouldRemoveFileUploadPrefix,
      isUnifiedFieldEnabled,
    });
  };

  const dynamicColumns: GridColDef[] = variantLabels.map((variantId) => {
    const variantLabel = getVariantLabel(variantId, variantLabelById);

    return {
      field: variantId,
      headerName: variantLabel,
      minWidth: 100,
      sortingOrder: ['asc', 'desc'],
      renderHeader: () => renderHeader(variantId, variantLabel),
      valueGetter: (params) => params.row.variants[variantId]?.value || 0,
      renderCell: (params) => renderCell(params, variantId),
      type: 'number',
    };
  });

  return dynamicColumns;
}
